import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Card } from './ui/card';
import LanguageSwitcher from './LanguageSwitcher';
import DetailDialog from './landing/DetailDialog';
import ContactSection from './landing/ContactSection';
import StatsStrip from './landing/StatsStrip';
import FAQSection from './landing/FAQSection';
import {
  ArrowRight, ShieldCheck, BarChart3, Users, Sparkles,
  Layers, Target, BookOpen, Wallet, ClipboardCheck,
  HeartHandshake, Globe2, FileText, Award, Building2, UserCog,
  Briefcase, ChevronRight, Newspaper, Calendar,
} from 'lucide-react';

/**
 * Public, informative landing page for the ISISP platform.
 *
 * Replaces the previous login-as-homepage. The Sign In / Register flow
 * still lives at /login — this page exposes a header CTA that navigates
 * users there.
 */
const LandingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [openModule, setOpenModule] = useState(null);  // module key whose detail dialog is open
  const [openCap, setOpenCap] = useState(null);        // capability key whose detail dialog is open
  const [openPersona, setOpenPersona] = useState(null); // persona key whose detail dialog is open
  const [openNews, setOpenNews] = useState(null);       // news item key whose detail dialog is open

  // Smooth-scroll for in-page anchor links
  useEffect(() => {
    const handler = (e) => {
      const target = e.target.closest('a[href^="#"]');
      if (!target) return;
      const id = target.getAttribute('href').slice(1);
      const el = document.getElementById(id);
      if (el) {
        e.preventDefault();
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  const modules = [
    { icon: Users,         key: 'social_actors' },
    { icon: Wallet,        key: 'investors' },
    { icon: HeartHandshake,key: 'participatory' },
    { icon: BookOpen,      key: 'capacity' },
    { icon: Layers,        key: 'combined' },
  ];

  const capabilities = [
    { icon: UserCog,       key: 'multi_role' },
    { icon: BarChart3,     key: 'kpi' },
    { icon: ShieldCheck,   key: 'certification' },
    { icon: Globe2,        key: 'bilingual' },
    { icon: Target,        key: 'sroi' },
    { icon: Sparkles,      key: 'matchmaking' },
    { icon: FileText,      key: 'deliverables' },
    { icon: ClipboardCheck,key: 'compliance' },
  ];

  const personas = [
    { icon: ShieldCheck, key: 'admin' },
    { icon: Wallet,      key: 'investor' },
    { icon: Building2,   key: 'social_actor' },
    { icon: Briefcase,   key: 'pm' },
  ];

  const news = [
    { key: 'item1', date: t('landing.news.item1.date'), category: t('landing.news.item1.category') },
    { key: 'item2', date: t('landing.news.item2.date'), category: t('landing.news.item2.category') },
    { key: 'item3', date: t('landing.news.item3.date'), category: t('landing.news.item3.category') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-emerald-50/30 text-slate-800">
      {/* ───────────────────── Sticky Header ───────────────────── */}
      <header
        className="sticky top-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200"
        data-testid="landing-header"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <a href="#hero" className="flex items-center" data-testid="landing-logo">
            <img
              src="/assets/isisp_logo.png"
              alt="ISISP"
              className="h-14 md:h-16 w-auto"
            />
          </a>

          <nav className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#about"        className="hover:text-emerald-700 transition" data-testid="nav-about">{t('landing.nav.about')}</a>
            <a href="#modules"      className="hover:text-emerald-700 transition" data-testid="nav-modules">{t('landing.nav.modules')}</a>
            <a href="#capabilities" className="hover:text-emerald-700 transition" data-testid="nav-capabilities">{t('landing.nav.capabilities')}</a>
            <a href="#personas"     className="hover:text-emerald-700 transition" data-testid="nav-personas">{t('landing.nav.personas')}</a>
            <a href="#news"         className="hover:text-emerald-700 transition" data-testid="nav-news">{t('landing.nav.news')}</a>
            <a href="#faq"          className="hover:text-emerald-700 transition" data-testid="nav-faq">{t('landing.nav.faq')}</a>
            <a href="#contact-form" className="hover:text-emerald-700 transition" data-testid="nav-contact-form">{t('landing.nav.contact_form')}</a>
            <a href="#contact"      className="hover:text-emerald-700 transition" data-testid="nav-contact">{t('landing.nav.contact')}</a>
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Button
              onClick={() => navigate('/login')}
              className="bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm rounded-full px-5"
              data-testid="header-signin-btn"
            >
              {t('landing.cta.sign_in')}
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </header>

      {/* ───────────────────── Hero ───────────────────── */}
      <section id="hero" className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,_rgba(16,185,129,0.12),_transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 md:pt-14 md:pb-24">
          <div className="max-w-4xl mx-auto text-center">
            {/* Big ISISP project logo (matches login page) */}
            <div className="flex justify-center mb-5">
              <img
                src="/assets/isisp_logo.png"
                alt="ISISP"
                className="h-28 md:h-32 w-auto drop-shadow-sm"
                data-testid="hero-isisp-logo"
              />
            </div>

            {/* RRF / Greece 2.0 — NextGenerationEU strip
                Per RRF visibility guidelines, the funding logo must appear above
                the fold (directly below the project logo, no scrolling required).
                Clicking the logo opens the official RRF Greece 2.0 portal. */}
            <div className="flex justify-center mb-8">
              <a
                href="https://greece20.gov.gr/"
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-3 rounded-xl bg-white border border-slate-200 shadow-sm transition-transform hover:scale-[1.03] hover:border-slate-300 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
                data-testid="hero-rrf-logo-link"
                title="Greece 2.0 — National Recovery and Resilience Plan (opens in a new tab)"
                aria-label="Greece 2.0 — NextGenerationEU — visit greece20.gov.gr"
              >
                <img
                  src="/greece_nextgen_logo.png"
                  alt="Greece 2.0 — National Recovery and Resilience Plan — Funded by the European Union — NextGenerationEU"
                  className="h-16 md:h-20 w-auto"
                  data-testid="hero-funding-logo"
                />
              </a>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-tight">
              {t('landing.hero.title_pre')}{' '}
              <span className="bg-gradient-to-r from-emerald-600 via-teal-600 to-sky-600 bg-clip-text text-transparent">
                {t('landing.hero.title_highlight')}
              </span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              {t('landing.hero.tagline')}
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-3 items-center justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/login')}
                className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-7 shadow-md"
                data-testid="hero-get-started-btn"
              >
                {t('landing.cta.get_started')} <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <a
                href="#about"
                className="inline-flex items-center gap-1 px-5 py-2.5 rounded-full text-slate-700 hover:bg-slate-100 transition text-sm font-medium"
                data-testid="hero-learn-more-link"
              >
                {t('landing.cta.learn_more')} <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── Live Stats Strip ───────────────────── */}
      <StatsStrip />

      {/* ───────────────────── About ───────────────────── */}
      <section id="about" className="py-20 md:py-24 bg-white border-t border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <div>
              <p className="text-emerald-700 text-sm font-semibold tracking-wider uppercase mb-3">
                {t('landing.about.eyebrow')}
              </p>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 leading-tight">
                {t('landing.about.title')}
              </h2>
            </div>
            <div className="space-y-5 text-slate-600 leading-relaxed text-base">
              <p>{t('landing.about.p1')}</p>
              <p>{t('landing.about.p2')}</p>
              <p>{t('landing.about.p3')}</p>
            </div>
          </div>

          {/* Promo video embed — STRATIS Facebook Reel */}
          <div className="mt-14 max-w-3xl mx-auto text-center" data-testid="about-promo-video">
            <h3 className="text-xl md:text-2xl font-semibold text-slate-900 mb-5">
              {t('landing.about.video_heading')}
            </h3>
            <div className="relative w-full overflow-hidden rounded-xl shadow-lg border border-slate-200 bg-slate-100" style={{ paddingTop: '56.25%' }}>
              <iframe
                title={t('landing.about.video_heading')}
                src="https://www.facebook.com/plugins/video.php?href=https%3A%2F%2Fwww.facebook.com%2Freel%2F3133113237035175&show_text=false&autoplay=false&width=560"
                className="absolute inset-0 w-full h-full"
                loading="lazy"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                referrerPolicy="strict-origin-when-cross-origin"
                data-testid="about-promo-iframe"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ───────────────────── Modules ───────────────────── */}
      <section id="modules" className="py-20 md:py-24 bg-slate-50/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-emerald-700 text-sm font-semibold tracking-wider uppercase mb-3">
              {t('landing.modules.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('landing.modules.title')}</h2>
            <p className="mt-4 text-slate-600">{t('landing.modules.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {modules.map(({ icon: Icon, key }) => (
              <Card
                key={key}
                className="p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-slate-200 bg-white flex flex-col"
                data-testid={`module-card-${key}`}
              >
                <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center mb-4 shadow-sm">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-slate-900 text-base mb-2">{t(`landing.modules.${key}.title`)}</h3>
                <p className="text-sm text-slate-600 leading-relaxed flex-1">{t(`landing.modules.${key}.desc`)}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpenModule(key)}
                  className="mt-4 self-start px-0 h-8 text-emerald-700 hover:text-emerald-800 hover:bg-transparent font-semibold"
                  data-testid={`module-learn-more-${key}`}
                >
                  {t('landing.modules.learn_more')} <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── Capabilities ───────────────────── */}
      <section id="capabilities" className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-emerald-700 text-sm font-semibold tracking-wider uppercase mb-3">
              {t('landing.capabilities.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('landing.capabilities.title')}</h2>
            <p className="mt-4 text-slate-600">{t('landing.capabilities.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {capabilities.map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="flex flex-col p-5 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-all"
                data-testid={`cap-${key}`}
              >
                <div className="flex items-start gap-3">
                  <div className="shrink-0 w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-emerald-700" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm mb-1">{t(`landing.capabilities.${key}.title`)}</h3>
                    <p className="text-xs text-slate-600 leading-relaxed">{t(`landing.capabilities.${key}.desc`)}</p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpenCap(key)}
                  className="mt-3 self-start px-0 h-7 text-emerald-700 hover:text-emerald-800 hover:bg-transparent text-xs font-semibold"
                  data-testid={`cap-learn-more-${key}`}
                >
                  {t('landing.modules.learn_more')} <ChevronRight className="w-3 h-3 ml-0.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── Personas ───────────────────── */}
      <section id="personas" className="py-20 md:py-24 bg-slate-900 text-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-emerald-400 text-sm font-semibold tracking-wider uppercase mb-3">
              {t('landing.personas.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-white">{t('landing.personas.title')}</h2>
            <p className="mt-4 text-slate-500">{t('landing.personas.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {personas.map(({ icon: Icon, key }) => (
              <div
                key={key}
                className="p-6 rounded-xl bg-slate-800/60 border border-slate-700 hover:border-emerald-500/50 hover:bg-slate-800 transition-all flex flex-col"
                data-testid={`persona-${key}`}
              >
                <Icon className="w-7 h-7 text-emerald-400 mb-4" />
                <h3 className="font-bold text-white mb-2">{t(`landing.personas.${key}.title`)}</h3>
                <p className="text-sm text-slate-500 leading-relaxed flex-1">{t(`landing.personas.${key}.desc`)}</p>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => setOpenPersona(key)}
                  className="mt-4 self-start px-0 h-7 text-emerald-400 hover:text-emerald-300 hover:bg-transparent text-xs font-semibold"
                  data-testid={`persona-learn-more-${key}`}
                >
                  {t('landing.modules.learn_more')} <ChevronRight className="w-3 h-3 ml-0.5" />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── News ───────────────────── */}
      <section id="news" className="py-20 md:py-24 bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="text-emerald-700 text-sm font-semibold tracking-wider uppercase mb-3 inline-flex items-center gap-2 justify-center">
              <Newspaper className="w-4 h-4" /> {t('landing.news.eyebrow')}
            </p>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('landing.news.title')}</h2>
            <p className="mt-4 text-slate-600">{t('landing.news.subtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {news.map((n) => (
              <Card
                key={n.key}
                className="overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-slate-200 bg-white flex flex-col"
                data-testid={`news-card-${n.key}`}
              >
                <div className="h-32 bg-gradient-to-br from-emerald-500 via-teal-500 to-sky-500 flex items-center justify-center relative">
                  <Newspaper className="w-10 h-10 text-white/90" />
                  <span className="absolute top-3 left-3 px-2 py-1 rounded-md bg-white/95 text-xs font-semibold text-emerald-700 uppercase tracking-wider">
                    {n.category}
                  </span>
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{n.date}</span>
                  </div>
                  <h3 className="font-bold text-slate-900 text-base mb-2 leading-snug">
                    {t(`landing.news.${n.key}.title`)}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed flex-1">
                    {t(`landing.news.${n.key}.summary`)}
                  </p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setOpenNews(n.key)}
                    className="mt-3 self-start px-0 h-7 text-emerald-700 hover:text-emerald-800 hover:bg-transparent text-xs font-semibold"
                    data-testid={`news-learn-more-${n.key}`}
                  >
                    {t('landing.modules.learn_more')} <ChevronRight className="w-3 h-3 ml-0.5" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ───────────────────── FAQ ───────────────────── */}
      <FAQSection />

      {/* ───────────────────── Contact / Request a Demo ───────────────────── */}
      <ContactSection />

      {/* ───────────────────── Final CTA ───────────────────── */}
      <section id="contact" className="py-20 md:py-24 bg-gradient-to-br from-emerald-50 via-white to-teal-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award className="w-12 h-12 text-emerald-700 mx-auto mb-5" />
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {t('landing.final.title')}
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto leading-relaxed">
            {t('landing.final.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 items-center justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/login')}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-7 shadow-md"
              data-testid="final-signin-btn"
            >
              {t('landing.cta.sign_in')}
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/login?mode=register')}
              className="rounded-full px-7 border-emerald-300 text-emerald-700 hover:bg-emerald-50"
              data-testid="final-register-btn"
            >
              {t('landing.cta.register')}
            </Button>
          </div>
        </div>
      </section>

      {/* ───────────────────── Footer ───────────────────── */}
      <footer className="bg-slate-950 text-slate-500 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-4 gap-8 text-sm">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <img src="/assets/isisp_logo.png" alt="ISISP" className="h-9 w-auto" />
              <span className="text-white font-bold text-lg">ISISP</span>
            </div>
            <p className="text-xs leading-relaxed">{t('landing.footer.about')}</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">{t('landing.footer.platform')}</h4>
            <ul className="space-y-2 text-xs">
              <li><a href="#about" className="hover:text-emerald-400">{t('landing.nav.about')}</a></li>
              <li><a href="#modules" className="hover:text-emerald-400">{t('landing.nav.modules')}</a></li>
              <li><a href="#capabilities" className="hover:text-emerald-400">{t('landing.nav.capabilities')}</a></li>
              <li><Link to="/certified-actors" className="hover:text-emerald-400">{t('landing.footer.public_registry')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">{t('landing.footer.access')}</h4>
            <ul className="space-y-2 text-xs">
              <li><Link to="/login" className="hover:text-emerald-400">{t('landing.cta.sign_in')}</Link></li>
              <li><Link to="/login?mode=register" className="hover:text-emerald-400">{t('landing.cta.register')}</Link></li>
              <li><Link to="/forgot-password" className="hover:text-emerald-400">{t('landing.footer.forgot_password')}</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-3 text-sm">{t('landing.footer.funding')}</h4>
            <p className="text-xs leading-relaxed">{t('landing.footer.funding_text')}</p>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-10 pt-6 border-t border-slate-800 text-xs text-slate-500 flex flex-col sm:flex-row justify-between gap-2">
          <span>© {new Date().getFullYear()} ISISP. {t('landing.footer.rights')}</span>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link
              to="/accessibility"
              className="hover:text-emerald-400 underline-offset-2 hover:underline"
              data-testid="footer-accessibility-link"
            >
              {t('landing.footer.accessibility', 'Accessibility Statement')}
            </Link>
            <span>{t('landing.footer.tagline')}</span>
          </div>
        </div>
      </footer>
      {/* ───────────────────── Detail dialogs (modules / capabilities / personas) ───────────────────── */}
      <DetailDialog
        open={!!openModule}
        onOpenChange={(o) => !o && setOpenModule(null)}
        icon={(modules.find((m) => m.key === openModule) || {}).icon || Layers}
        iconBg="gradient"
        title={openModule ? t(`landing.modules.${openModule}.title`) : ''}
        tagline={openModule ? t(`landing.modules.${openModule}.desc`) : ''}
        intro={openModule ? t(`landing.modules.${openModule}.detail.intro`) : ''}
        features={openModule ? t(`landing.modules.${openModule}.detail.features`, { returnObjects: true }) : []}
        callout={openModule ? {
          label: t('landing.modules.detail.who_label'),
          text: t(`landing.modules.${openModule}.detail.who`),
        } : null}
        benefits={openModule ? t(`landing.modules.${openModule}.detail.benefits`, { returnObjects: true }) : []}
        testId="module-detail-dialog"
      />

      <DetailDialog
        open={!!openCap}
        onOpenChange={(o) => !o && setOpenCap(null)}
        icon={(capabilities.find((c) => c.key === openCap) || {}).icon || Sparkles}
        iconBg="soft"
        title={openCap ? t(`landing.capabilities.${openCap}.title`) : ''}
        tagline={openCap ? t(`landing.capabilities.${openCap}.desc`) : ''}
        intro={openCap ? t(`landing.capabilities.${openCap}.detail.intro`) : ''}
        features={openCap ? t(`landing.capabilities.${openCap}.detail.features`, { returnObjects: true }) : []}
        callout={openCap ? {
          label: t('landing.capabilities.detail.use_case_label'),
          text: t(`landing.capabilities.${openCap}.detail.use_case`),
        } : null}
        testId="cap-detail-dialog"
      />

      <DetailDialog
        open={!!openPersona}
        onOpenChange={(o) => !o && setOpenPersona(null)}
        icon={(personas.find((p) => p.key === openPersona) || {}).icon || Users}
        iconBg="soft"
        title={openPersona ? t(`landing.personas.${openPersona}.title`) : ''}
        tagline={openPersona ? t(`landing.personas.${openPersona}.desc`) : ''}
        intro={openPersona ? t(`landing.personas.${openPersona}.detail.intro`) : ''}
        features={openPersona ? t(`landing.personas.${openPersona}.detail.modules_used`, { returnObjects: true }) : []}
        featuresLabel={t('landing.personas.detail.modules_used_label')}
        callout={openPersona ? {
          label: t('landing.personas.detail.day_in_life_label'),
          text: t(`landing.personas.${openPersona}.detail.day_in_life`),
        } : null}
        ctaLabel={openPersona ? t(`landing.personas.${openPersona}.detail.cta_label`) : null}
        testId="persona-detail-dialog"
      />

      <DetailDialog
        open={!!openNews}
        onOpenChange={(o) => !o && setOpenNews(null)}
        icon={Newspaper}
        iconBg="gradient"
        title={openNews ? t(`landing.news.${openNews}.title`) : ''}
        tagline={openNews ? `${t(`landing.news.${openNews}.category`)} · ${t(`landing.news.${openNews}.date`)}` : ''}
        intro={openNews ? t(`landing.news.${openNews}.detail.body`) : ''}
        features={openNews ? t(`landing.news.${openNews}.detail.highlights`, { returnObjects: true }) : []}
        featuresLabel={t('landing.news.detail.highlights_label')}
        callout={openNews ? {
          label: t('landing.news.detail.whats_next_label'),
          text: t(`landing.news.${openNews}.detail.whats_next`),
        } : null}
        testId="news-detail-dialog"
      />
    </div>
  );
};

export default LandingPage;
